import { Configure, InstantSearch, useClearRefinements, useInstantSearch, useSearchBox } from 'react-instantsearch';
import { type FC, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { File, FolderSearch, Search, Search as SearchIcon } from 'lucide-react';
import { type SearchClient } from 'algoliasearch/lite';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { emptySearchResponse, isTokenlessQuery } from '@/utilities/search/suppressEmptySearch';
import { defaultPaginationIndex, defaultPaginationSize } from '@/types/pagination';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { getGlobalSearchFilterClauses } from '@/utilities/search/filters';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useVariantUrls } from '@/hooks/data/assets/useVariantUrls';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { useSearchClient } from '@/context/SearchClientContext';
import { AssetThumbnail } from '@/components/asset-thumbnail';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { convertSearchResults } from '@/components/home';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeyboardKey } from '@/lib/keyboard';
import { EntityType } from '@/types/entity';
import { DAMNodeType } from '@/types/tree';
import { Variants } from '@/types/asset';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

export const QuickSearch: FC = () => {
  const { searchClient: baseSearchClient, searchIndexes } = useSearchClient();

  const searchClient = useMemo(
    () =>
      ({
        ...baseSearchClient,
        search(requests: Parameters<SearchClient['search']>[0]) {
          // Don't fire search queries for empty search terms (page load, bar focus without text)
          // or for queries with no searchable characters, which Typesense would match-all (DM-2485).
          if (requests.every((req) => !req.params?.query || isTokenlessQuery(req.params.query))) {
            return Promise.resolve(emptySearchResponse(requests));
          }
          return baseSearchClient.search(requests);
        },
      }) as unknown as SearchClient,
    [baseSearchClient],
  );

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={searchIndexes.default}
      future={{ preserveSharedStateOnUnmount: true }}
    >
      <Configure
        attributesToRetrieve={['*']}
        // Limiting search dropdown list to 10 results
        hitsPerPage={10}
        filters={'trashed:false'}
      />
      <SearchContent />
    </InstantSearch>
  );
};

const SearchContent = () => {
  const [openSearch, setOpenSearch] = useState<boolean>(false);
  const searchQuery = useSearchStore((state) => state.searchQuery);
  const search = useIsSearching();
  const tSearch = useTranslations('search');
  const instance = useInstantSearch();
  const hitIds = useMemo(() => (instance?.results?.hits ?? []).map((h: any) => h.id), [instance?.results?.hits]);
  const { variantUrls } = useVariantUrls(hitIds);
  const results = useMemo(
    () => convertSearchResults(instance?.results?.hits, {}, variantUrls),
    [instance?.results?.hits, variantUrls],
  );

  const selectedNode = useTreeStore((state) => state.selectedNode);

  const { setSelectedAssets } = useGallerySelection();
  const { setIsAllSelected, setLocateAssetId } = useBrowserStore();
  const { isTrashPage, isBrowsePage, isHomePage, onTrashSearch } = useCurrentPage();
  const { setSearchQuery } = useSearchStore();
  const { setPageIndex } = useTableStore();
  const { push, query, replace, pathname } = useRouter();
  const { refine } = useSearchBox();
  const { refine: clearRefinements } = useClearRefinements();

  const inputContainerRef = useRef<HTMLInputElement>(null);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const refineDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(refineDebounceRef.current), []);

  useEffect(() => {
    if (!inputContainerRef.current) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setInputWidth(entry.contentRect.width);
      }
    });
    observer.observe(inputContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const navigateInSearch = () => {
    setSelectedAssets([]);
    setIsAllSelected(false);
    setLocateAssetId(undefined);
    setPageIndex(EntityType.Asset, defaultPaginationIndex);
  };

  useEffect(() => {
    const value = typeof query?.query === 'string' ? query.query : '';
    setSearchQuery(value);
    setOpenSearch(false);
  }, [query.query]);

  const handleGlobalSearch = () => {
    // Clearing all refinements before navigation is crucial to ensure consistency between the dropdown search
    // results and the search page. This prevents discrepancies where certain results appear in the dropdown
    clearRefinements();

    const separator = ' && ';
    const preserved = getGlobalSearchFilterClauses(String(query.filters ?? ''));
    const { filters: _filters, ...rest } = query;

    const newQuery = {
      ...rest,
      pageIndex: 1,
      query: searchQuery.trim(),
      ...(preserved.length > 0 ? { filters: preserved.join(separator) } : {}),
    };

    navigateInSearch();
    void replace(
      {
        pathname: '/browse',
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  const handleSearchInParent = () => {
    clearRefinements();
    let scopeFilter = '';
    if (isTrashPage || onTrashSearch) {
      scopeFilter = 'trashed:true';
    } else if (selectedNode) {
      scopeFilter =
        selectedNode.type === DAMNodeType.Folder
          ? `parentId:"${selectedNode.node.id}"`
          : `albumIds:"${selectedNode.node.id}"`;
    }

    const separator = ' && ';
    const preserved = getGlobalSearchFilterClauses(String(query.filters ?? ''));
    const newFilters = [scopeFilter, ...preserved].filter(Boolean).join(separator);

    const newQuery = {
      ...query,
      pageIndex: 1,
      query: searchQuery.trim(),
      filters: newFilters,
    };

    navigateInSearch();
    void replace(
      {
        pathname: isBrowsePage ? pathname : '/browse',
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KeyboardKey.Enter) {
      if (search && (selectedNode?.node || isTrashPage)) {
        handleSearchInParent();
      } else {
        handleGlobalSearch();
      }
    }
  };

  return (
    <Popover open={openSearch}>
      <PopoverAnchor asChild>
        <Input
          prependIcon={<SearchIcon className="mr-2 icon-6 shrink-0 opacity-50" strokeWidth={1.5} />}
          ref={inputContainerRef}
          aria-expanded={openSearch}
          placeholder={tSearch('assetsPlaceholder')}
          value={searchQuery}
          onChange={(e) => {
            const value = e.target?.value || '';
            setSearchQuery(value);
            if (!openSearch && value !== searchQuery) {
              setOpenSearch(true);
            }
            clearTimeout(refineDebounceRef.current);
            refineDebounceRef.current = setTimeout(() => refine(value.trim()), 200);
          }}
          className="h-9 max-w-2xl min-w-48 flex-1 rounded-full border-black/25 bg-input-background px-3 focus-within:ring-offset-ui-panel dark:border-white/25"
          inputFieldClassName="text-lg font-normal placeholder:text-text-gray-alternate"
          onFocus={() => {
            refine(searchQuery.trim());
            setOpenSearch(true);
          }}
          onBlur={() => setOpenSearch(false)}
          blurOnEnter={false}
          onKeyDown={handleKeyDown}
          isLoading={instance.status === 'loading'}
        />
      </PopoverAnchor>
      <PopoverContent
        className="max-w-2xl min-w-48 rounded-none p-0"
        style={inputWidth ? { width: inputWidth } : undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <Command className="rounded-none">
          <CommandList className="scrollbar-none">
            <CommandEmpty>
              {!searchQuery.trim()
                ? tSearch('startTyping')
                : searchQuery.trim() !== (instance.results?.query ?? '') ||
                    instance.status === 'loading' ||
                    instance.status === 'stalled'
                  ? tSearch('searching')
                  : tSearch('noAssetsFound')}
            </CommandEmpty>
            {results.length > 0 && <CommandSeparator />}
            <CommandGroup>
              {results.map((asset) => (
                <CommandItem
                  key={asset.id}
                  value={`asset-${asset.id}`}
                  onSelect={() => {
                    const origin = isHomePage
                      ? ''
                      : isBrowsePage
                        ? Array.isArray(query.slug)
                          ? `browse/${query.slug.join('/')}`
                          : ''
                        : (pathname.replace(/^\/+/, '') ?? '');
                    void push({
                      pathname: `/asset/${asset.id}`,
                      query: {
                        origin,
                        pageIndex: query.pageIndex ?? defaultPaginationIndex + 1,
                        pageSize: query.pageSize ?? defaultPaginationSize,
                        ...(query.filters ? { filters: query.filters } : {}),
                        ...(query.query || query.query === '' ? { query: query.query } : {}),
                        ...(query.searchFilters || query.searchFilters === ''
                          ? { searchFilters: query.searchFilters }
                          : {}),
                      },
                    });
                    setOpenSearch(false);
                  }}
                >
                  {asset.variants?.[Variants.ThumbnailInternal]?.url ? (
                    <AssetThumbnail
                      id={asset.id}
                      asset={asset}
                      variantKey={Variants.ThumbnailInternal}
                      type={asset.type}
                      fill
                      imageContainerClassName="mr-2 size-4 h-[48px] w-[48px]! min-h-[48px] min-w-[48px] relative"
                      imageClassName="w-full h-full min-h-full min-w-full transition-all object-cover"
                      iconContainerClassName="flex h-[48px] w-[48px]! min-h-[48px] min-w-[48px] items-center justify-center h-full"
                      iconClassName="stroke-ui-light icon-12"
                      playIconClassName="hidden"
                      iconStrokeWidth={1}
                      isListView
                    />
                  ) : (
                    <File className="mr-2 icon-4" />
                  )}
                  <div className="flex w-full flex-col">
                    <div className="flex items-center justify-between">
                      <p className="line-clamp-1 text-sm font-bold text-text-base">{asset.name}</p>
                      {/* <Badge variant="outline" className="py-0">
                        PNG
                      </Badge> */}
                    </div>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <p className="line-clamp-1 text-sm text-text-gray">{asset.description}</p>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent className="text-balance">{asset.description} </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {
            <div className="z-50 flex items-center justify-items-stretch">
              {(selectedNode ?? (isTrashPage || onTrashSearch)) && (
                <Button
                  className="grow"
                  variant={search ? 'search-action-primary' : 'search-action-secondary'}
                  onClick={() => {
                    handleSearchInParent();
                  }}
                  icon={FolderSearch}
                  iconPosition="right"
                  iconClassName="icon-6"
                  data-cy="search-in-parent-button"
                >
                  <span className="w-full text-left text-sm">
                    {tSearch('searchInScope', {
                      scope:
                        isTrashPage || onTrashSearch
                          ? 'trash'
                          : selectedNode?.type === DAMNodeType.Folder
                            ? 'folder'
                            : 'album',
                    })}
                  </span>
                </Button>
              )}
              <Button
                className="grow"
                variant={
                  search && (selectedNode || isTrashPage || onTrashSearch)
                    ? 'search-action-secondary'
                    : 'search-action-primary'
                }
                onClick={() => {
                  handleGlobalSearch();
                }}
                icon={Search}
                iconPosition="right"
                iconClassName="icon-6"
                data-cy="search-everywhere-button"
              >
                <span className="w-full text-left text-sm font-bold">{tSearch('everywhere')}</span>
              </Button>
            </div>
          }
        </Command>
      </PopoverContent>
    </Popover>
  );
};
